import React from 'react';
import { Package, ChevronRight, Box } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Order } from '../../types/order.types';

interface OrderHistoryListProps {
    orders: Order[];
}

const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
        case 'DELIVERED':
            return 'bg-green-100/50 text-green-600 dark:bg-green-500/10 dark:text-green-400';
        case 'SHIPPED':
        case 'IN_TRANSIT':
            return 'bg-blue-100/50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400';
        case 'PENDING':
        case 'PROCESSING':
            return 'bg-amber-100/50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400';
        case 'CANCELLED':
            return 'bg-red-100/50 text-red-600 dark:bg-red-500/10 dark:text-red-400';
        default:
            return 'bg-gray-100/50 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
    }
};

export const OrderHistoryList: React.FC<OrderHistoryListProps> = ({ orders }) => {
    if (orders.length === 0) {
        return (
            <div className="text-center py-12 bg-gray-50/50 dark:bg-gray-900/50 rounded-2xl border border-dashed border-border/60">
                <Package className="size-10 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No orders yet</p>
                <Link to="/" className="text-sm font-black text-primary uppercase tracking-widest mt-4 inline-block hover:underline">
                    Explore Products
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {orders.map((order) => (
                <div
                    key={order.id}
                    className="p-6 bg-white dark:bg-black rounded-2xl border border-border/40 hover:border-border/80 transition-all shadow-sm group"
                >
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex gap-4">
                            <div className="size-12 rounded-xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-400 border border-border/40">
                                <Box className="size-6" />
                            </div>
                            <div>
                                <h3 className="font-black text-lg text-[#111418] dark:text-white uppercase tracking-tight">
                                    ORD-{order.id.slice(0, 8).toUpperCase()}
                                </h3>
                                <p className="text-sm font-medium text-gray-400">
                                    {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                            </div>
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
                            {order.status.toLowerCase().replace('_', ' ')}
                        </span>
                    </div>

                    <div className="flex items-end justify-between">
                        <div className="space-y-1">
                            {/* item count fallback */}
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                Multiple Items
                            </p>
                            <p className="text-xl font-black text-[#111418] dark:text-white">${order.total_amount.toFixed(2)}</p>
                        </div>
                        <Link
                            to={`/orders/${order.id}`}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-border/60 font-black text-xs uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                        >
                            <ChevronRight className="size-4" /> View Details
                        </Link>
                    </div>
                </div>
            ))}
        </div>
    );
};
